import { useEffect, useState } from 'react';
import { TrendingUp, AlertTriangle, ShieldCheck, Ban, Activity } from 'lucide-react';
import api from '../api/api';

interface SignalStat {
  signal: string;
  total: number;
  wins: number;
  losses: number;
  winRate: number;
}

export const StrategyPerformance: React.FC = () => {
  const [stats, setStats] = useState<SignalStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/analytics/signals');
        const sorted = res.data.sort((a: SignalStat, b: SignalStat) => b.total - a.total);
        setStats(sorted);
      } catch (e) {
        console.error('Failed to load strategy stats', e);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStrategyStatus = (winRate: number, total: number) => {
    if (total < 3) return { label: 'LEARNING', color: 'text-gray-400', icon: <Activity size={12} />, bg: 'bg-gray-500/10', border: 'border-gray-500/20' };
    if (winRate >= 70) return { label: 'BOOST (+10)', color: 'text-emerald-400', icon: <TrendingUp size={12} />, bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' };
    if (winRate >= 45) return { label: 'ACTIVE', color: 'text-blue-400', icon: <ShieldCheck size={12} />, bg: 'bg-blue-500/10', border: 'border-blue-500/20' };
    if (winRate >= 30) return { label: 'RISKY (-15)', color: 'text-orange-400', icon: <AlertTriangle size={12} />, bg: 'bg-orange-500/10', border: 'border-orange-500/20' };
    return { label: 'BANNED (-30)', color: 'text-red-500', icon: <Ban size={12} />, bg: 'bg-red-500/10', border: 'border-red-500/20' };
  };

  if (loading) return <div className="animate-pulse h-40 bg-slate-800/50 rounded-2xl"></div>;

  return (
    <div className="bg-[#1e293b] border border-slate-800 p-6 rounded-[24px] shadow-xl w-full">
      <div className="flex items-center justify-between mb-6">
        <span className="text-[10px] text-slate-500 bg-slate-800/50 px-2 py-1 rounded-lg border border-slate-700">
          Based on last {stats.reduce((acc, s) => acc + s.total, 0)} trades
        </span>
      </div>

      {/* ✅ СЕТКА: Изменено с flex-col на grid. 1 колонка на моб, 2 на планшетах, 4 на десктопах */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.length === 0 ? (
          <div className="col-span-full text-center py-8 text-slate-500 text-sm italic">
            📭 No trading history yet. AI is waiting for data.
          </div>
        ) : (
          stats.map((stat) => {
            const status = getStrategyStatus(stat.winRate, stat.total);

            return (
              <div key={stat.signal} className={`p-4 rounded-xl bg-slate-900/40 border ${status.border} hover:bg-slate-800/50 transition-all duration-300 flex flex-col justify-between`}>

                <div className="flex justify-between items-start mb-3">
                  <span className="font-mono font-bold text-xs text-slate-200">{stat.signal}</span>
                  <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md border border-white/5 ${status.bg} ${status.color}`}>
                    {status.icon}
                    <span className="text-[9px] font-bold tracking-wider">{status.label}</span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px]">
                    <span className="text-slate-500">Win Rate</span>
                    <span className={`font-mono font-bold ${stat.winRate >= 50 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {stat.winRate.toFixed(1)}%
                    </span>
                  </div>

                  <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-1000 ${stat.winRate >= 50 ? 'bg-emerald-500' : 'bg-red-500'}`}
                      style={{ width: `${stat.winRate}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex justify-between items-center text-[9px] text-slate-500 font-mono mt-3 pt-2 border-t border-slate-800/50">
                  <span>Total: <strong className="text-slate-300">{stat.total}</strong></span>
                  <div className="flex gap-2">
                    <span className="text-emerald-500/80">W:<strong>{stat.wins}</strong></span>
                    <span className="text-red-500/80">L:<strong>{stat.losses}</strong></span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};